import { ControlledDto } from 'src/Libs/flow/installation/api'
import { ChargingMode, ChargingSchedule } from 'src/Libs/flow/installation/forms'
import { getChargingStationDepartureTimeToDto, getModbusTcpDepartureTime } from 'src/Libs/flow/installation/utils'

type SunModeDto = ControlledDto['parameters']['sunMode']

export const parseChargingModeSettings = (
  sunMode: SunModeDto
): {
  chargingMode: ChargingMode
  chargingWeeklySchedule: ChargingSchedule
} => {
  return {
    chargingMode: parseChargingStrategy(sunMode),
    chargingWeeklySchedule: parseWeeklySchedule(sunMode),
  }
}

export const parseChargingStrategy = (sunMode: SunModeDto): ChargingMode => {
  const isSunModeActivated = !!sunMode?.activated
  const chargingStrategy = sunMode?.chargingStrategy

  if (isSunModeActivated) {
    if (chargingStrategy === 'Disabled') return 'pvOnly'
    if (chargingStrategy === 'Immediate') return 'pvImmediate'
    if (chargingStrategy === 'Delayed') return 'pvDelayed'
  }
  return 'standard'
}

const parseWeeklySchedule = (sunMode: SunModeDto): ChargingSchedule => {
  const parameters = sunMode?.parameterList

  if (!parameters) return []

  const weekDaysByIndex: (keyof ChargingSchedule[number]['weekDays'])[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ]

  const weeklyScheduleEntries = new Map<string, ChargingSchedule[number]>()

  for (let index = 0; index < parameters.length; index++) {
    const parameter = parameters[index]
    /** Entries with the same daytime and minEnergy will be grouped together. */
    const key = `${parameter.daytime}-${parameter.minEnergy}`

    if (weeklyScheduleEntries.has(key)) {
      const previousValue = weeklyScheduleEntries.get(key)!
      weeklyScheduleEntries.set(key, {
        ...previousValue,
        weekDays: { ...previousValue.weekDays, [weekDaysByIndex[index]]: true },
      })
    } else {
      weeklyScheduleEntries.set(key, {
        departureTime: getModbusTcpDepartureTime(parameter.daytime ?? '0'),
        energyWanted: parseFloat(parameter.minEnergy) || 0,
        weekDays: {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false,
          [weekDaysByIndex[index]]: true,
        },
      })
    }
  }

  /* Remove the element with energy 0 and departure time 0 as it is a leftover element. */
  weeklyScheduleEntries.delete('-0')
  weeklyScheduleEntries.delete('0-0')
  return [...weeklyScheduleEntries.values()]
}

/** Form charging station (Evse) to controlled POST or PUT Dto settings. */
export const getSunMode = (
  holdingTime: number,
  chargingMode: ChargingMode,
  safePvSchedule: ChargingSchedule,
  efficientPvSchedule: ChargingSchedule
): SunModeDto => {
  const holdingTimeInMin = holdingTime

  if (chargingMode === 'pvOnly') {
    return {
      holdingTimeInMin,
      activated: true,
      chargingStrategy: 'Disabled',
      parameterList: [],
    }
  }

  if (chargingMode === 'pvImmediate') {
    /* Keep only minEnergy and discard daytime. */
    return {
      holdingTimeInMin,
      activated: true,
      chargingStrategy: 'Immediate',
      parameterList: parseWeeklyScheduleToDto(safePvSchedule).map(schedule => ({
        minEnergy: schedule.minEnergy,
      })),
    }
  }

  if (chargingMode === 'pvDelayed') {
    return {
      holdingTimeInMin,
      activated: true,
      chargingStrategy: 'Delayed',
      parameterList: parseWeeklyScheduleToDto(efficientPvSchedule),
    }
  }

  return {
    holdingTimeInMin,
    activated: false,
    chargingStrategy: 'Disabled',
    parameterList: [],
  }
}

const parseWeeklyScheduleToDto = (
  weeklySchedule: ChargingSchedule
): NonNullable<NonNullable<ControlledDto['parameters']['sunMode']>['parameterList']> => {
  const daysToIndex: Record<keyof ChargingSchedule[number]['weekDays'], number> = {
    monday: 0,
    tuesday: 1,
    wednesday: 2,
    thursday: 3,
    friday: 4,
    saturday: 5,
    sunday: 6,
  }

  const dayEntries = Object.entries(daysToIndex)

  const dayTimes: NonNullable<NonNullable<ControlledDto['parameters']['sunMode']>['parameterList']> = [
    { daytime: '0', minEnergy: '0' },
    { daytime: '0', minEnergy: '0' },
    { daytime: '0', minEnergy: '0' },
    { daytime: '0', minEnergy: '0' },
    { daytime: '0', minEnergy: '0' },
    { daytime: '0', minEnergy: '0' },
    { daytime: '0', minEnergy: '0' },
  ]

  for (const schedule of weeklySchedule) {
    const days = schedule.weekDays

    for (const [day, index] of dayEntries) {
      //Cast day to keyof type
      if (days[day as keyof typeof days]) {
        dayTimes[index].minEnergy = (schedule.energyWanted || 0).toString()

        if (schedule.departureTime.trim() !== '') {
          dayTimes[index].daytime = getChargingStationDepartureTimeToDto(schedule.departureTime)
        }
      }
    }
  }

  return dayTimes
}
